import { type InputHTMLAttributes, type ReactNode } from 'react'
import clsx from 'clsx'

/** Aceita lista simples (label = value) OU lista com value/label separados. */
type FieldOption = string | { value: string; label: string }

interface Props extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  label: string
  value: string | number
  onChange: (value: string) => void
  type?: string
  options?: FieldOption[]
  rows?: number
  full?: boolean
  note?: string
  error?: string
  children?: ReactNode
}

export function Field({
  label, value, onChange, type = 'text', options, rows,
  full, note, error, required, placeholder, ...rest
}: Props) {
  const id = `f-${label.replace(/\s/g, '-').toLowerCase()}`
  const baseInput = clsx(
    'w-full bg-surface border rounded-lg px-3 py-2.5 text-sm',
    'text-text-primary placeholder:text-text-muted',
    'focus:outline-none focus:border-accent transition',
    error ? 'border-danger' : 'border-border'
  )
  return (
    <div className={clsx(full && 'col-span-full')}>
      <label htmlFor={id} className="text-xs font-bold uppercase text-text-secondary block mb-1.5">
        {label}{required && <span className="text-danger ml-0.5">*</span>}
      </label>
      {options ? (
        <select id={id} value={value} onChange={e => onChange(e.target.value)} className={baseInput}>
          {options.map(o => {
            const val = typeof o === 'string' ? o : o.value
            const lab = typeof o === 'string' ? o : o.label
            return <option key={val} value={val}>{lab}</option>
          })}
        </select>
      ) : rows ? (
        <textarea
          id={id}
          value={value}
          onChange={e => onChange(e.target.value)}
          rows={rows}
          placeholder={placeholder}
          className={clsx(baseInput, 'resize-y leading-relaxed')}
        />
      ) : (
        <input
          id={id}
          type={type}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          className={baseInput}
          {...rest}
        />
      )}
      {note && <p className="text-xs text-text-muted mt-1">{note}</p>}
      {error && <p className="text-xs text-danger mt-1">{error}</p>}
    </div>
  )
}
